import type { ISiteTagsDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { FilterValueLifecycleStatus } from "app/pages/sales-intelligence/domain/inclusion/types";
import {
    calculateInclusionConditionsScalarDelta,
    enforceAtMostOneExcludeCondition,
    isExcludeInclusionAvailable,
    isInclusionConditionFullyRemoved,
} from "app/pages/sales-intelligence/domain/inclusion/inclusionFilterConditionUtils";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { normalizeInclusionConditionArrayValue } from "../../utils/normalizeInclusionConditionArrayValue";
import { hasSameSiteTagsObject } from "app/pages/sales-intelligence/domain/leads/utils/site-tags-operations";

import { SiteTagsFilterValue } from "./SiteTagsFilterValue";

class SiteTagsFilterModel extends FilterPanelBaseFilter<ISiteTagsDto[], undefined> {
    isExcludeInclusionAvailable(conditions?: ISiteTagsDto[], editing?: ISiteTagsDto | null): boolean {
        return isExcludeInclusionAvailable(this.fromApiValue(conditions), editing ?? null);
    }
    fromApiValue(value?: readonly ISiteTagsDto[] | ISiteTagsDto): ISiteTagsDto[] {
        return normalizeInclusionConditionArrayValue(value);
    }

    toApiValue(value?: ISiteTagsDto[]): ISiteTagsDto[] | undefined {
        const normalizedValue = this.fromApiValue(value);
        const filteredValue = enforceAtMostOneExcludeCondition(
            normalizedValue.filter((item) => !new SiteTagsFilterValue(item).isEmpty()),
        );

        return filteredValue.length ? filteredValue : undefined;
    }

    isValueEmpty(value?: readonly ISiteTagsDto[] | ISiteTagsDto): boolean {
        const normalizedValue = this.fromApiValue(value);

        return !normalizedValue.length || normalizedValue.every((item) => new SiteTagsFilterValue(item).isEmpty());
    }

    calculateValueDelta(
        previousValue: ISiteTagsDto[] | undefined,
        newValue: ISiteTagsDto[] | undefined,
    ): FilterValueDelta<string> {
        return calculateInclusionConditionsScalarDelta(
            this.fromApiValue(previousValue),
            this.fromApiValue(newValue),
            (item) => item.values,
            hasSameSiteTagsObject,
        );
    }

    getValueLifecycleStatus<V = ISiteTagsDto[]>(
        value?: V,
        valueDelta?: FilterValueDelta<V>,
    ): FilterValueLifecycleStatus {
        const condition = value as ISiteTagsDto | undefined;
        const scalarDelta = valueDelta as FilterValueDelta<string> | undefined;

        if (!condition) {
            return "added";
        }

        return isInclusionConditionFullyRemoved(condition.values, scalarDelta) ? "removed" : "added";
    }
}

export default SiteTagsFilterModel;
